import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Sharing: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{t('metadata.sharing.title')}</title>
        <meta name="description" content={t('metadata.sharing.description')} />
        <meta name="keywords" content="share IPTV playlist, M3U sharing, HLS stream sharing, IPTV Player sharing, playlist management, secure IPTV sharing, stream sharing guide" />
        <link rel="canonical" href="https://iptvplayer.stream/sharing" />
      </Helmet>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-gray-800 dark:text-white">Share Your IPTV Playlists Securely</h1>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700 dark:text-gray-200">Easy IPTV Playlist Sharing</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Our web-based M3U player makes sharing your IPTV playlists simple and secure. Whether you want to share specific channels 
            or entire playlists, our HLS-compatible sharing system ensures your content is accessible only to intended recipients.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-700 dark:text-gray-200">How to Share Your Playlists</h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">1. Access Share Options</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/click_shareButton.png" 
                alt={t('altText.shareButton')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Click the share button in your playlist interface to begin the sharing process. Our M3U player provides 
              flexible sharing options for your IPTV streams, supporting various formats including HLS and DASH.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">2. Select Channels to Share</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/select_channels_to_share.png" 
                alt={t('altText.selectChannels')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Choose which channels you want to share from your playlist. You can select individual channels or entire groups, 
              making it perfect for creating custom IPTV playlists for sharing.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">3. Get Shareable Link</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/playlist_link_to_share.png" 
                alt={t('altText.copyLink')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              A unique shareable link will be generated for your selected channels. This link can be used to access the 
              shared playlist in any M3U-compatible player or directly in our web-based streaming interface.
            </p>
          </div>
        </section>

        <section className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-blue-800 dark:text-blue-300">Sharing Best Practices</h2>
          <ul className="list-disc ml-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li>Share only with trusted recipients to maintain playlist security</li>
            <li>Use channel selection to create customized sharing experiences</li>
            <li>Generate new share links periodically for better security</li>
          </ul>
        </section>

        <section className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Need Help with Sharing?</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Having trouble sharing your IPTV playlists or need assistance with our sharing features? Check out our{' '}
            <Link 
              to="/m3u-hls-player-faq" 
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              {t('footer.faq', 'FAQ')}
            </Link>
            {' '}or{' '}
            <Link 
              to="/contact" 
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              contact our support team
            </Link>
            {' '}for help with managing and sharing your IPTV playlists online.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Sharing; 